import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader, PageLayout } from '@/widgets/layout/ui/AppLayout'
import { BotBanner } from '@/entities/bot'
import { Button, Card } from '@/shared/ui'
import { ApiError } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { toast } from '@/shared/model/toast'
import { callsApi, useCall } from '@/entities/call'
import { ReportCard } from '@/entities/call/ui/ReportCard'
import { useProfile } from '@/entities/profile'
import { useMinutes } from '@/entities/minutes'
import { formatMinSec } from '@/shared/lib/minutes'
import { CallSession, microphoneAvailable } from '@/features/test-call/lib/session'
import { useCallStore } from '@/features/test-call/model'
import { CallStage } from '@/features/test-call/ui/CallStage'
import { LiveStats } from '@/features/test-call/ui/LiveStats'
import styles from './TestCallPage.module.css'

/**
 * Проверка агента голосом.
 *
 * Это единственное место, где клиент слышит то, что услышит его
 * собеседник. Всё остальное в кабинете — текст и настройки, по которым
 * невозможно понять, звучит агент живо или как автоответчик.
 */
export function TestCallPage() {
  const [starting, setStarting] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneDialing, setPhoneDialing] = useState(false)
  const [finishedCallId, setFinishedCallId] = useState<number | null>(null)
  const sessionRef = useRef<CallSession | null>(null)
  const queryClient = useQueryClient()

  const profile = useProfile()
  const minutes = useMinutes()
  const testLimited = (minutes.data?.testDailyMinutes ?? 0) > 0
  const testLeftMs = minutes.data?.testLeftMs ?? 0
  const testOut = testLimited && testLeftMs < 1000
  const { status, begin, setStatus, setLevel, handle, reset } = useCallStore()
  const finished = useCall(finishedCallId)

  const live = status === 'connecting' || status === 'live'
  const ready = Boolean(profile.data?.business?.trim() || profile.data?.offerings?.trim())

  // Разговор нельзя оставлять открытым при уходе со страницы: микрофон
  // остался бы включённым, а звонок — незакрытым.
  useEffect(() => {
    return () => {
      sessionRef.current?.stop()
      sessionRef.current = null
      reset()
    }
  }, [reset])

  async function start() {
    setStarting(true)
    setFinishedCallId(null)

    try {
      const call = await callsApi.startTest()
      begin(call.callId)

      const session = new CallSession({
        onEvent: handle,
        onLevel: setLevel,
        onStatus: (next, detail) => {
          setStatus(next, detail)
          if (next === 'ended') {
            // Звонок закрыт — открываем отчёт. Он появится через
            // несколько секунд: разбор идёт в фоне, и страница сама
            // его дождётся.
            setFinishedCallId(call.callId)
            void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
            void queryClient.invalidateQueries({ queryKey: queryKeys.minutes })
          }
        },
      })

      sessionRef.current = session
      await session.start(call.mediaUrl, call.eventsUrl)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось начать звонок')
      reset()
    } finally {
      setStarting(false)
    }
  }

  // Звонок на настоящий телефон через SIP-транк. В отличие от тестового,
  // звук идёт не в браузер, а в телефонную линию: разговор ведёт агент,
  // а итог смотрят в разделе «Звонки».
  async function dialPhone() {
    const number = phoneNumber.trim()
    if (!number) {
      toast.error('Введите номер телефона')
      return
    }
    setPhoneDialing(true)
    try {
      const pass = await callsApi.startPhone()
      const r = await fetch(pass.dialUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ number, token: pass.token }),
      })
      if (!r.ok) throw new Error(await r.text())
      toast.success('Звоним на ' + number + '. Итог появится в разделе «Звонки».')
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
      void queryClient.invalidateQueries({ queryKey: queryKeys.minutes })
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось позвонить: ' + String(error))
    } finally {
      setPhoneDialing(false)
    }
  }

  function hangUp() {
    sessionRef.current?.stop()
    sessionRef.current = null
  }

  return (
    <PageLayout aside={live ? <LiveStats /> : undefined}>
      <PageHeader
        title="Тестовый звонок"
        description="Поговорите с агентом так, как это сделает ваш клиент. Он ведёт разговор по вашей анкете — можно возражать, отказывать и перебивать на полуслове."
      />
      <BotBanner />

      {!microphoneAvailable() && (
        <Card>
          <div className={styles.notice}>
            {/* Ограничение браузера, не наше. Сказать об этом надо до
                нажатия кнопки: иначе отказ выглядит поломкой сайта. */}
            Браузер даёт доступ к микрофону только по защищённому соединению. Откройте
            кабинет по https или через localhost — тогда тестовый звонок заработает.
          </div>
        </Card>
      )}

      <Card
        title="Позвонить на телефон"
        subtitle="Агент сам наберёт номер и проведёт разговор по вашей анкете. Звук идёт в трубку собеседника, а итог появится в разделе «Звонки»."
      >
        <div className={styles.dialRow}>
          <input
            type="tel"
            className={styles.dialInput}
            placeholder="+7 900 000-00-00"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void dialPhone() }}
            disabled={phoneDialing}
          />
          <Button onClick={dialPhone} loading={phoneDialing} disabled={!ready}>
            Позвонить
          </Button>
        </div>
        {!ready && (
          <div className={styles.notice}>
            Анкета почти пустая — агенту не о чем говорить. Заполните её на странице настройки.
          </div>
        )}
      </Card>

      {live ? (
        <Card>
          <CallStage onHangUp={hangUp} />
        </Card>
      ) : (
        <Card>
          <div className={styles.start}>
            <div className={styles.startIcon}>
              <PhoneIcon />
            </div>

            <div className={styles.startText}>
              <h2 className={styles.startTitle}>
                {finishedCallId ? 'Позвонить ещё раз' : 'Готовы услышать агента?'}
              </h2>
              {testLimited && (
                <p className={styles.startHint} role={testOut ? 'status' : undefined}>
                  {testOut
                    ? `Тестовые звонки на сегодня закончились (${minutes.data?.testDailyMinutes} мин в день). Завтра лимит обновится.`
                    : `Тестовых минут на сегодня: ${formatMinSec(testLeftMs)} из ${minutes.data?.testDailyMinutes}:00.`}
                </p>
              )}
              <p className={styles.startHint}>
                {ready
                  ? 'Агент заговорит первым. Разрешите доступ к микрофону, когда браузер спросит.'
                  : 'Анкета почти пустая — агенту не о чем говорить. Заполните её, иначе разговор выйдет ни о чём.'}
              </p>
            </div>

            <div className={styles.startActions}>
              <Button
                onClick={start}
                loading={starting}
                disabled={!microphoneAvailable() || testOut}
                variant={ready ? 'primary' : 'secondary'}
              >
                Позвонить
              </Button>
              {!ready && (
                <Link className={styles.link} to="/settings/profile">
                  Заполнить анкету
                </Link>
              )}
            </div>
          </div>
        </Card>
      )}

      {finishedCallId && !live && (
        <Card
          title="Чем закончился разговор"
          subtitle="Итог определила модель по стенограмме. Если она ошиблась — поправьте, это влияет на статистику"
          action={
            <Link className={styles.link} to={`/calls/${finishedCallId}`}>
              Открыть стенограмму
            </Link>
          }
        >
          {finished.data ? (
            <ReportCard call={finished.data.call} />
          ) : (
            <div className={styles.notice}>Загружаем отчёт…</div>
          )}
        </Card>
      )}
    </PageLayout>
  )
}

function PhoneIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <path
        d="M9.2 3.5L11.3 8.2L9.1 10.4C10.2 12.8 13.2 15.8 15.6 16.9L17.8 14.7L22.5 16.8V21C22.5 21.8 21.8 22.5 21 22.5C11.3 22 4 14.7 3.5 5C3.5 4.2 4.2 3.5 5 3.5H9.2Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  )
}
